import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * 30-slot cohort visualisation.
 * Each slot has one of three states: confirmed, reserved, open.
 * Numbers shown only on confirmed/reserved slots — open slots show "?".
 */

// Deterministic distribution so layout is stable across reloads.
// 5 confirmed · 8 reserved · 17 open  = 30 total
const STATE_MAP = (() => {
  const map = {};
  const confirmed = new Set([3, 7, 12, 18, 24]);
  const reserved = new Set([2, 5, 9, 14, 19, 22, 27, 30]);
  for (let i = 1; i <= 30; i++) {
    if (confirmed.has(i)) map[i] = "confirmed";
    else if (reserved.has(i)) map[i] = "reserved";
    else map[i] = "open";
  }
  return map;
})();

export default function Cohort() {
  const slots = useMemo(() => Array.from({ length: 30 }, (_, i) => i + 1), []);

  const scrollToForm = () => {
    const el = document.getElementById("apply");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="the-thirty"
      data-testid="cohort-section"
      className="relative bg-cream py-16 lg:py-24"
    >
      <div className="max-w-[1400px] mx-auto container-x">
        {/* eyebrow */}
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono text-[10px] tracking-[0.28em] text-burgundy">THE THIRTY</span>
          <span className="flex-1 h-px bg-line" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12 lg:mb-14">
          <div className="lg:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="font-display font-semibold text-[clamp(2rem,5vw,4rem)] leading-[1.04] tracking-[-0.015em] text-ink mb-10"
            >
              Thirty coveted slots. Honoring the industry&rsquo;s leading
              distributors and suppliers, by invitation and nomination.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="text-[15px] lg:text-[16px] leading-[1.8] text-body max-w-2xl"
            >
              This isn&rsquo;t just any list&mdash;it&rsquo;s a carefully
              curated cross-section designed to showcase true excellence:
              featuring legacy giants alongside agile disruptors from every
              corner of the market. While a handful of key slots are already
              secured, the remaining invitations are now open.
            </motion.p>
          </div>

          <div className="lg:col-span-4 lg:pt-3 lg:text-right">
            <p className="font-mono text-[12px] leading-[1.7] text-burgundy">
              Cohort fills on a
              <br />
              rolling, first-confirmed basis
            </p>
          </div>
        </div>

        {/* grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          data-testid="cohort-grid"
          className="grid grid-cols-6 md:grid-cols-10 lg:grid-cols-12 gap-2 sm:gap-3"
        >
          {slots.map((n) => {
            const state = STATE_MAP[n];
            const isOpen = state === "open";
            const onClick = isOpen ? scrollToForm : undefined;
            return (
              <button
                key={n}
                type="button"
                onClick={onClick}
                tabIndex={isOpen ? 0 : -1}
                data-testid={`slot-${n}-${state}`}
                className={`slot slot--${state}`}
                aria-label={`Slot ${n} ${state}`}
              >
                {state === "confirmed" && (
                  <>
                    <span className="slot__num">{String(n).padStart(2, "0")}</span>
                    <span className="text-base text-ink">✓</span>
                  </>
                )}
                {state === "reserved" && (
                  <span className="slot__num !static !top-auto !right-auto !text-[11px] !text-muted-ink">
                    {String(n).padStart(2, "0")}
                  </span>
                )}
                {state === "open" && (
                  <>
                    <span className="text-lg text-burgundy">?</span>
                    <span className="slot__tip">Open — apply below</span>
                  </>
                )}
              </button>
            );
          })}
        </motion.div>

        {/* legend */}
        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[11px] tracking-[0.05em] text-ink-soft">
          <LegendItem variant="confirmed" label="Confirmed" />
          <LegendItem variant="reserved" label="Reserved / in conversation" />
          <LegendItem variant="open" label="Open — could be you" />
        </div>
      </div>
    </section>
  );
}

function LegendItem({ variant, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className={`block w-5 h-7 slot--${variant}`} style={{ aspectRatio: "auto" }} />
      <span>{label}</span>
    </div>
  );
}
